import React, { createRef } from "react";
import { BryntumGantt } from "@bryntum/gantt-react";
import { createHistogram, generateDataset, getDepartmentFilters, getGanttConfig } from "./ganttConfig";
import styles from "../../css/modules/projectOverview.module.scss";
import { connect } from "react-redux";
import { updateTasks } from "../Project/utils/saveChanges";
import { InitialToolbar } from "./components/InitialToolBar";
import { Loading } from "../../components/Loading";
import { AsyncHelper, ProjectModel } from "@bryntum/gantt";
import { withPageApi } from "./components/PageApi";
import { Allotment } from "allotment";
import { withAccess } from "../../utils/withAccess";
import { AccessEntries, PAGE_ACCESS } from "../../utils/accessEntries";
import { checkResources, applyColumnFilters } from "../../utils/ganttUtils";
import { ApiClientContext } from '../../components/ApiProvider';
import { ViewTypes } from "../../utils/constants";
import { DateHelper } from "@bryntum/gantt";

class ProjectOverview extends React.Component {
  static contextType = ApiClientContext;
  originalData = null; // gantt data from last api call
  histogramRef = React.createRef();
  allotmentRef = createRef();
  ref = createRef();
  oldCalculations = null; 

  constructor(props) {
    super(props);
    this.configs = this.props.setting.screenLayoutMultiViewConfig;
    this.generalProjectSettings = this.props.setting.generalProjectSettings;
    this.ganttConfig = getGanttConfig(this.configs, this.generalProjectSettings, this);
    this.state = {
      project: null,
      loading: false,
      showHistogram: !!this.configs.showHistogram,
      exportInProgress: false,
    };
  }

  get histogram() {
    return this.props.api.histogram;
  }

  get gantt() {
    return this.props.api.gantt;
  }

  get pageTitle() {
    return this.props.pageTitle || "";
  }

  componentDidUpdate(prevProps) {
    if (this.props.setting.screenLayoutMultiViewConfig !== prevProps.setting.screenLayoutMultiViewConfig) {
      this.configs = this.props.setting.screenLayoutMultiViewConfig;
    }
  }

  async componentWillUnmount() {
    if (this.oldCalculations) {
      let calc = await this.state.project.setCalculations(this.oldCalculations);
      console.log(calc);
    }
  }

  applyDepartmentsFilter = (departments) => {
    return new Promise((res) => {
      if (this.gantt?.taskStore) {
        if (departments.length) {
          this.gantt.taskStore.addFilter(getDepartmentFilters(departments));
        } else {
          this.gantt.taskStore.removeFilter("tasks-department-filter");
        }
      } else {
        res();
      }
    });
  };

  /**
   * @param props {{}}
   * @param [props.readyData] {{}}
   * @param [props.params] {{}}
   * @param [props.departments] {{}}
   * @returns {Promise<void>}
   */
  configureGantt = async ({ readyData, params, departments }) => {
    let data;
    this.ganttConfig = getGanttConfig(this.configs, this.generalProjectSettings, this);
    if (!readyData) {
      data = await generateDataset(params, this.context);
      this.originalData = JSON.parse(JSON.stringify(data)); // create a copy of dataset
    } else {
      data = JSON.parse(JSON.stringify(readyData));
    }
    checkResources(this, data.resourcesData);
    await AsyncHelper.sleep(10);

    const project = new ProjectModel(data);
    project.on({dataReady: () => {
      this.applyDepartmentsFilter(departments);
      applyColumnFilters(params?.columnFilters, project.taskStore);
    }});
    
    if (params?.oldIndicators) {
      for (let key in this.ganttConfig.indicatorsFeature.items) {
        if ((params?.oldIndicators[key] != null) && (params?.oldIndicators[key] != undefined) && params?.oldIndicators[key] !== this.ganttConfig.indicatorsFeature.items[key]) {
          this.ganttConfig.indicatorsFeature.items[key] = params?.oldIndicators[key];
        }
      }
      if (params?.oldIndicators.viewPresetId) {
        this.ganttConfig.viewPreset = params.oldIndicators.viewPresetId;  
      }
    }    

    let previousCalculations = await project.setCalculations({
      // Disable task percentDone, startDate, endDate and duration fields automatic calculation
      tasks: {
        percentDone: "userProvidedValue",
        startDate: "userProvidedValue",
        endDate: "userProvidedValue",
        effort: "userProvidedValue",
        earlyStartDate: "userProvidedValue",
        earlyEndDate: "userProvidedValue",
        lateStartDate: "userProvidedValue",
        lateEndDate: "userProvidedValue",
        totalSlack: "userProvidedValue",
        critical: "userProvidedValue",
      },
    });
    if (!this.oldCalculations) { //only set the first time
      this.oldCalculations = previousCalculations;
    }

    this.setState({ project, loading: false }, async () => {
      if (this.state.showHistogram) {
        await this.createHistogram();
      }
      this.applyEvents(departments, params?.columnFilters);
    });
  };

  once = true;
  applyEvents = async () => {
    const gantt = this.props.api.gantt;
    //for this dev reasons:
    window.gantt = gantt;
    if (this.histogram) {
      const preserveVisibleStart =
        gantt?.currentOrientation?.visibleDateRange?.startDate != null
          ? new Date(gantt.currentOrientation.visibleDateRange.startDate.getTime())
          : null;
      this.histogram.addPartner(gantt);
      if (preserveVisibleStart && typeof gantt.shiftToDate === "function") {
        gantt.shiftToDate(preserveVisibleStart, null);
        if (typeof gantt.nextAnimationFrame === "function") {
          await gantt.nextAnimationFrame();
        }
      }
    }
    this.props.update({
      showToolbar: true,
    });
    const project = this.state.project;
    project.on({
      refresh: async () => {
        if (this.once) {
          this.once = false;
          if (this.configs.zoomToFitOnLoad) {
            gantt.zoomToFit({
              leftMargin: 50,
              rightMargin: 50,
            });
          }

          if (this.configs.showHistogram) {
            await this.createHistogram();
          }
        }
      },
      hasChanges: () => {
        const updatedTasks = project.changes?.tasks?.updated;
        if (updatedTasks) {
          const taskWithRollup = updatedTasks.filter((i) => i.hasOwnProperty("rollup") || i.hasOwnProperty("note"));
          if (taskWithRollup.length) {
            const onlyPropsToUpdate = taskWithRollup.map((i) => {
              if (i.hasOwnProperty("rollup") && i.hasOwnProperty("note")) {
                return { id: i.id, rollup: i.rollup, note: i.note };
              }else  if (i.hasOwnProperty("rollup")) {
                return { id: i.id, rollup: i.rollup};
              } else if (i.hasOwnProperty("note")) {
                return { id: i.id, note: i.note };
              }             
            });
            updateTasks(onlyPropsToUpdate, {}, ViewTypes.ProjectsOverview, this.context);
          }
        }
        project.taskStore.commit();
      },
    });
    gantt.columns.on("move", () => {
      let max = this.gantt.columns.data.length;
      this.gantt.columns.forEach((el) => {
        el.data.order = max--;
      });
    });
    gantt.features.print.exportDialog.on({
      beforeShow: ({ source }) => {
        this.setState({
          exportInProgress: true,
        });
        source.widgetMap.rangeStartField.value = DateHelper.add(project.startDate, -7, 'day');
        source.widgetMap.rangeEndField.value = DateHelper.add(project.endDate, 7, 'day');

        source.widgetMap.paperFormatField.items.sort((a, b) => {
          const valueA = (a.value || '').toLowerCase();
          const valueB = (b.value || '').toLowerCase();
          return valueA.localeCompare(valueB);
        }); 
      },
      hide: () => {
        this.setState({
          exportInProgress: false,
        });
      },
    });
    this.resizeAllotments();
  };

  createHistogram = async () => {
    if (this.creatingHistogram) {
      return;
    }
    this.creatingHistogram = true;
    const { api, departmentsFilter, minOfferScoringPercentage, access } = this.props;
    const { gantt, setHistogram } = api;
    const readOnly = access?.AccessLevel === PAGE_ACCESS.Read;

    const histogram = await createHistogram({
      target: this.histogramRef.current,
      gantt: gantt,
      readOnly: readOnly,
      configs: this.configs,
      departmentsFilter,
      minOfferScoringPercentage,
    }, this.context);
    setHistogram(histogram);

    this.creatingHistogram = false;
  };

  resizeAllotments = () => {
    return new Promise((res) => {
      setTimeout(() => {
        if (this.allotmentRef.current && this.ref.current) {
          const root = this.allotmentRef.current.offsetHeight;
          const { showHistogram } = this.state;
          const rest = (root) / (showHistogram ? 2 : 1);
          this.ref.current.resize([rest, rest]);
          res();
        }
      });
    });
  };

  toggleHistogramVisible = (val) => {
    return this.setState(
      {
        showHistogram: val,
      },
      async () => {
        if (val) {
          await this.createHistogram();
        } else {
          const { histogram, setHistogram } = this.props.api;
          histogram?.destroy();
          setHistogram(null);
          this.histogramRef.current.innerHTML = "";
        }
        await this.resizeAllotments();
      }
    );
  };

  render() {
    const { project, loading, showHistogram } = this.state;
    const { api, access } = this.props;
    const readOnly = access?.AccessLevel === PAGE_ACCESS.Read;

    return (
      <div className={styles.root}>
        <InitialToolbar overview={this} />
        <div className={styles.overAllotment} ref={this.allotmentRef}>
          {project && (
            <Allotment ref={this.ref} vertical className={styles.componentsContainer} proportionalLayout={true}>
              <Allotment.Pane minSize={20} preferredSize={200} priority={1}>
                <BryntumGantt
                  {...this.ganttConfig}
                  readOnly={readOnly}
                  ref={api.setGantt}
                  project={project}
                  className={styles.gantt}
                  minHeight={100}
                />
              </Allotment.Pane>
              <Allotment.Pane preferredSize={"40%"} minSize={100} priority={1} visible={showHistogram}>
                <div className={styles.histogramContainer} ref={this.histogramRef} data-testid="depview_histogram"></div>
              </Allotment.Pane>
            </Allotment>
          )}
        </div>
        {loading && <Loading />}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    pageTitle: state.pageTitle,
  };
};

export default connect(mapStateToProps)(withPageApi(withAccess(ProjectOverview, AccessEntries.TotalProjectOverview)));
